import * as R from 'ramda';
import { IJsonQuery, IJsonQueryRange, IJsonQueryTerm, IJsonQueryTerms, IJsonQueryType } from './types';
import { IGeoValue } from '../../types/filters/user_geo';
import { range, term, terms, geo } from './constructors';

export function setRootType(value: IJsonQueryType): (jsonQuery: IJsonQuery) => IJsonQuery {
  return jsonQuery => ({
    ...jsonQuery,
    _type: value,
  });
}

export function setTerm<T, K extends keyof IJsonQuery, V extends IJsonQuery[K] & IJsonQueryTerm<T>>(
  key: K,
): (jsonQuery: IJsonQuery) => (value: V['value'] | undefined) => IJsonQuery {
  return jsonQuery => value => R.assoc(key, term(value), jsonQuery) as IJsonQuery;
}

export function setTerms<T, K extends keyof IJsonQuery, V extends IJsonQuery[K] & IJsonQueryTerms<T>>(
  key: K,
): (jsonQuery: IJsonQuery) => (value: V['value']) => IJsonQuery {
  return jsonQuery => value => R.assoc(key, terms(value), jsonQuery) as IJsonQuery;
}

export function setRange<K extends keyof IJsonQuery, V extends IJsonQuery[K] & IJsonQueryRange>(
  key: K,
): (jsonQuery: IJsonQuery) => (gte: V['value']['gte'], lte: V['value']['lte']) => IJsonQuery {
  return jsonQuery => (gte, lte) => R.assoc(key, range(gte, lte), jsonQuery) as IJsonQuery;
}

export function setRangeMin<K extends keyof IJsonQuery, V extends IJsonQuery[K] & IJsonQueryRange>(
  key: K,
): (jsonQuery: IJsonQuery) => (gte: V['value']['gte']) => IJsonQuery {
  return jsonQuery => gte =>
    R.assoc(key, range(gte, R.pathOr(undefined, [key, 'value', 'lte'], jsonQuery)), jsonQuery) as IJsonQuery;
}

export function setRangeMax<K extends keyof IJsonQuery, V extends IJsonQuery[K] & IJsonQueryRange>(
  key: K,
): (jsonQuery: IJsonQuery) => (lte: V['value']['lte']) => IJsonQuery {
  return jsonQuery => lte =>
    R.assoc(key, range(R.pathOr(undefined, [key, 'value', 'gte'], jsonQuery), lte), jsonQuery) as IJsonQuery;
}

export function setGeo(): (jsonQuery: IJsonQuery) => (value: IGeoValue[]) => IJsonQuery {
  return jsonQuery => value => R.assoc('geo', geo(value), jsonQuery);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isTerm<T>(value: any): value is IJsonQueryTerm<T> {
  return typeof value === 'object' && value.type === 'term';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isTerms<T>(value: any): value is IJsonQueryTerms<T> {
  return typeof value === 'object' && value.type === 'terms';
}

export function getTermValue<T, K extends keyof IJsonQuery, V extends IJsonQuery[K] & IJsonQueryTerm<T>>(
  key: K,
): (jsonQuery: IJsonQuery) => V['value'] | undefined {
  return jsonQuery => {
    const termField = jsonQuery[key];

    return isTerm<T>(termField) ? (termField.value as V['value']) : undefined;
  };
}

export function getTermsValue<T, K extends keyof IJsonQuery, V extends IJsonQuery[K] & IJsonQueryTerms<T>>(
  key: K,
): (jsonQuery: IJsonQuery) => V['value'] {
  return jsonQuery => {
    const termsField = jsonQuery[key];

    return isTerms<T>(termsField) ? termsField.value : [];
  };
}
